/**
 * Seven friendly characters from the "Scoops & Smiles" asset kit: the bear
 * shopkeeper plus six customers (rabbit, cat, fox, panda, pig, frog). Every
 * face is happy and front-facing — no scary or sad states. All share the warm
 * outline + rosy cheeks so they read as one set.
 *
 * Each is authored in a 200×210 viewBox and bottom-anchored in game.
 */
import { INK } from '../palette.ts';
import { svgDoc } from '../primitives.ts';

const EYE = '#2c2320';
const CHEEK = '#F49AB0';

/** A shiny dark eye with a white catch-light (kit convention). */
const eye = (cx: number, cy: number): string =>
  `<ellipse cx="${cx}" cy="${cy}" rx="10" ry="13" fill="${EYE}"/><circle cx="${cx - 4}" cy="${cy - 5}" r="4" fill="#fff"/>`;

const cheek = (cx: number, cy: number, fill = CHEEK, op = 0.6): string =>
  `<ellipse cx="${cx}" cy="${cy}" rx="13" ry="8" fill="${fill}" opacity="${op}"/>`;

const SW = `stroke="${INK}" stroke-width="6"`;

function bear(): string {
  return svgDoc(
    200,
    210,
    `<circle cx="52" cy="58" r="28" fill="#A9743F" ${SW}/><circle cx="148" cy="58" r="28" fill="#A9743F" ${SW}/>` +
      `<circle cx="52" cy="58" r="14" fill="#C98F5E"/><circle cx="148" cy="58" r="14" fill="#C98F5E"/>` +
      `<circle cx="100" cy="115" r="70" fill="#A9743F" ${SW}/>` +
      `<ellipse cx="100" cy="140" rx="42" ry="32" fill="#E7C49A" ${SW}/>` +
      `<ellipse cx="100" cy="124" rx="13" ry="9" fill="#3a2a20"/>` +
      `<path d="M100,133 v7" stroke="${INK}" stroke-width="5" stroke-linecap="round"/>` +
      `<path d="M100,140 q-13,11 -25,2 M100,140 q13,11 25,2" fill="none" stroke="${INK}" stroke-width="5" stroke-linecap="round"/>` +
      eye(72, 103) +
      eye(128, 103) +
      cheek(58, 138) +
      cheek(142, 138) +
      `<path d="M100,190 L72,176 L72,204 Z" fill="#36B5A6" stroke="${INK}" stroke-width="5" stroke-linejoin="round"/>` +
      `<path d="M100,190 L128,176 L128,204 Z" fill="#36B5A6" stroke="${INK}" stroke-width="5" stroke-linejoin="round"/>` +
      `<circle cx="100" cy="190" r="9" fill="#2CA193" stroke="${INK}" stroke-width="5"/>`,
  );
}

function rabbit(): string {
  return svgDoc(
    200,
    210,
    `<g transform="rotate(-8 78 50)"><ellipse cx="78" cy="48" rx="17" ry="48" fill="#F6F0EA" ${SW}/><ellipse cx="78" cy="50" rx="8" ry="34" fill="#F6C0CE"/></g>` +
      `<g transform="rotate(8 122 50)"><ellipse cx="122" cy="48" rx="17" ry="48" fill="#F6F0EA" ${SW}/><ellipse cx="122" cy="50" rx="8" ry="34" fill="#F6C0CE"/></g>` +
      `<circle cx="100" cy="124" r="64" fill="#F6F0EA" ${SW}/>` +
      eye(76, 118) +
      eye(124, 118) +
      `<path d="M100,134 l-7,-7 h14 z" fill="#F49AB0" stroke="${INK}" stroke-width="3.5" stroke-linejoin="round"/>` +
      `<path d="M100,140 v6 M100,146 q-9,8 -16,1 M100,146 q9,8 16,1" fill="none" stroke="${INK}" stroke-width="4.5" stroke-linecap="round"/>` +
      cheek(62, 140) +
      cheek(138, 140),
  );
}

function cat(): string {
  return svgDoc(
    200,
    210,
    `<path d="M60,78 L46,30 L96,62 Z" fill="#ED9B4C" ${SW} stroke-linejoin="round"/>` +
      `<path d="M140,78 L154,30 L104,62 Z" fill="#ED9B4C" ${SW} stroke-linejoin="round"/>` +
      `<path d="M64,66 L56,42 L82,60 Z" fill="#F6C0CE"/><path d="M136,66 L144,42 L118,60 Z" fill="#F6C0CE"/>` +
      `<circle cx="100" cy="118" r="62" fill="#ED9B4C" ${SW}/>` +
      `<g stroke="#D97E2E" stroke-width="5" stroke-linecap="round" fill="none"><path d="M86,72 v14 M100,68 v16 M114,72 v14"/></g>` +
      eye(76, 114) +
      eye(124, 114) +
      `<path d="M100,130 l-6,-6 h12 z" fill="#F49AB0" stroke="${INK}" stroke-width="3" stroke-linejoin="round"/>` +
      `<path d="M100,136 v5 M100,141 q-9,8 -16,1 M100,141 q9,8 16,1" fill="none" stroke="${INK}" stroke-width="4.5" stroke-linecap="round"/>` +
      `<g stroke="#caa890" stroke-width="3.5" stroke-linecap="round" fill="none"><path d="M58,128 L30,122 M58,138 L30,140 M142,128 L170,122 M142,138 L170,140"/></g>` +
      cheek(60, 138, '#F26A8D', 0.5) +
      cheek(140, 138, '#F26A8D', 0.5),
  );
}

function fox(): string {
  return svgDoc(
    200,
    210,
    `<path d="M58,76 L42,26 L98,58 Z" fill="#E0703A" ${SW} stroke-linejoin="round"/>` +
      `<path d="M142,76 L158,26 L102,58 Z" fill="#E0703A" ${SW} stroke-linejoin="round"/>` +
      `<path d="M58,76 L50,46 L74,62 Z" fill="#5a3b2b"/><path d="M142,76 L150,46 L126,62 Z" fill="#5a3b2b"/>` +
      `<circle cx="100" cy="116" r="60" fill="#E0703A" ${SW}/>` +
      `<path d="M100,150 C70,150 50,134 56,110 C76,118 124,118 144,110 C150,134 130,150 100,150 Z" fill="#FBF4EC" stroke="${INK}" stroke-width="5" stroke-linejoin="round"/>` +
      `<ellipse cx="100" cy="142" rx="11" ry="8" fill="#3a2a20"/>` +
      `<path d="M100,150 q-8,7 -15,1 M100,150 q8,7 15,1" fill="none" stroke="${INK}" stroke-width="4.5" stroke-linecap="round"/>` +
      eye(76, 106) +
      eye(124, 106) +
      cheek(62, 128, '#F26A8D', 0.5) +
      cheek(138, 128, '#F26A8D', 0.5),
  );
}

function panda(): string {
  return svgDoc(
    200,
    210,
    `<circle cx="52" cy="58" r="26" fill="#2c2320" ${SW}/><circle cx="148" cy="58" r="26" fill="#2c2320" ${SW}/>` +
      `<circle cx="100" cy="118" r="66" fill="#FBFAF7" ${SW}/>` +
      `<ellipse cx="74" cy="112" rx="19" ry="25" fill="#2c2320" transform="rotate(-16 74 112)"/>` +
      `<ellipse cx="126" cy="112" rx="19" ry="25" fill="#2c2320" transform="rotate(16 126 112)"/>` +
      `<circle cx="76" cy="110" r="7" fill="#fff"/><circle cx="78" cy="112" r="3.5" fill="#2c2320"/>` +
      `<circle cx="124" cy="110" r="7" fill="#fff"/><circle cx="122" cy="112" r="3.5" fill="#2c2320"/>` +
      `<ellipse cx="100" cy="138" rx="10" ry="7" fill="#2c2320"/>` +
      `<path d="M100,145 v5 M100,150 q-8,7 -14,1 M100,150 q8,7 14,1" fill="none" stroke="${INK}" stroke-width="4.5" stroke-linecap="round"/>` +
      cheek(60, 140, CHEEK, 0.55) +
      cheek(140, 140, CHEEK, 0.55),
  );
}

function pig(): string {
  return svgDoc(
    200,
    210,
    `<path d="M54,82 L50,44 L92,66 Z" fill="#F4A6C0" ${SW} stroke-linejoin="round"/>` +
      `<path d="M146,82 L150,44 L108,66 Z" fill="#F4A6C0" ${SW} stroke-linejoin="round"/>` +
      `<circle cx="100" cy="120" r="64" fill="#F4A6C0" ${SW}/>` +
      `<ellipse cx="100" cy="138" rx="30" ry="22" fill="#F7BCD0" ${SW}/>` +
      `<ellipse cx="90" cy="138" rx="5" ry="8" fill="#D9789A"/><ellipse cx="110" cy="138" rx="5" ry="8" fill="#D9789A"/>` +
      eye(76, 108) +
      eye(124, 108) +
      cheek(58, 132, '#E8729A', 0.5) +
      cheek(142, 132, '#E8729A', 0.5),
  );
}

function frog(): string {
  return svgDoc(
    200,
    210,
    `<circle cx="68" cy="62" r="28" fill="#82C95B" ${SW}/><circle cx="132" cy="62" r="28" fill="#82C95B" ${SW}/>` +
      `<circle cx="100" cy="128" r="64" fill="#82C95B" ${SW}/>` +
      `<circle cx="68" cy="60" r="14" fill="#fff"/><circle cx="70" cy="62" r="7" fill="#2c2320"/>` +
      `<circle cx="132" cy="60" r="14" fill="#fff"/><circle cx="130" cy="62" r="7" fill="#2c2320"/>` +
      `<circle cx="86" cy="120" r="3" fill="${INK}"/><circle cx="114" cy="120" r="3" fill="${INK}"/>` +
      `<path d="M68,138 q32,30 64,0" fill="none" stroke="${INK}" stroke-width="6" stroke-linecap="round"/>` +
      cheek(62, 138, CHEEK, 0.5) +
      cheek(138, 138, CHEEK, 0.5),
  );
}

const BUILDERS: Record<string, () => string> = {
  bear,
  rabbit,
  cat,
  fox,
  panda,
  pig,
  frog,
};

export function animal(name: string): string {
  return (BUILDERS[name] ?? bear)();
}

export function animalTextures(): Record<string, { svg: string; w: number; h: number }> {
  const out: Record<string, { svg: string; w: number; h: number }> = {};
  for (const name of Object.keys(BUILDERS)) {
    out[`animal_${name}`] = { svg: animal(name), w: 200, h: 210 };
  }
  return out;
}

/** All seven, in kit order. */
export const ANIMAL_NAMES = Object.keys(BUILDERS);

/** The fixed shopkeeper behind the counter. */
export const SHOPKEEPER = 'bear';

/** The six customers that rotate through the queue (everyone but the bear). */
export const CUSTOMER_NAMES = ANIMAL_NAMES.filter((n) => n !== SHOPKEEPER);
